import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Observable, of, take, tap } from 'rxjs';
import {
  LicensedComponentDto,
  LicensedProductComponentDto,
  LicensedProductDto,
} from '../models/license.model';
import { LicensedProductService } from './licensed-product.service';

type LicensedProductState = {
  products: LicensedProductDto[];
  availableComponents: LicensedProductComponentDto[];
  selectedProduct: LicensedProductDto | null;
  loading: boolean;
  saving: boolean;
  error: unknown | null;
};

const initialState: LicensedProductState = {
  products: [],
  availableComponents: [],
  selectedProduct: null,
  loading: false,
  saving: false,
  error: null,
};

export interface GetLicensedProductPayload {
  productId: string;
}

export interface UpdateLicensedProductPayload {
  productId: string;
  payload: LicensedProductDto;
}

export const LicensedProductStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasProducts: computed(() => store.products().length > 0),
    busy: computed(() => store.loading() || store.saving()),
  })),
  withMethods(
    (store, licensedProductService = inject(LicensedProductService)) => {
      const normalizeAvailableComponents = (
        components: LicensedProductComponentDto[],
      ): LicensedProductComponentDto[] => {
        const seenIds = new Set<string>();
        return components.reduce<LicensedProductComponentDto[]>(
          (acc, component) => {
            const normalizedId = component.id?.trim();
            if (!normalizedId || seenIds.has(normalizedId)) {
              return acc;
            }
            seenIds.add(normalizedId);

            acc.push({
              id: normalizedId,
              componentName: component.componentName?.trim() || normalizedId,
              description: component.description?.trim() || '',
              kind: component.kind,
            });
            return acc;
          },
          [],
        );
      };

      const normalizeLicensedComponents = (
        components: unknown,
      ): LicensedComponentDto[] | undefined => {
        if (!Array.isArray(components)) {
          return undefined;
        }

        const seenIds = new Set<string>();
        const normalized = components
          .map((component) => {
            let candidate: string | null = null;
            let source: Partial<LicensedComponentDto> = {};
            if (typeof component === 'string') {
              candidate = component.trim();
            } else if (typeof component === 'object' && component !== null) {
              const value = component as Record<string, unknown>;
              source = component as Partial<LicensedComponentDto>;
              const keys = [
                'component',
                'name',
                'id',
                'code',
                'value',
                'label',
              ];
              for (const key of keys) {
                const nestedCandidate = value[key];
                if (
                  typeof nestedCandidate === 'string' &&
                  nestedCandidate.trim()
                ) {
                  candidate = nestedCandidate.trim();
                  break;
                }
              }
            }

            if (!candidate || seenIds.has(candidate)) {
              return null;
            }

            seenIds.add(candidate);
            return { ...source, id: candidate } as LicensedComponentDto;
          })
          .filter(
            (component): component is LicensedComponentDto =>
              component !== null,
          );

        return normalized;
      };

      const normalizeProduct = (
        product: LicensedProductDto,
      ): LicensedProductDto => ({
        ...product,
        licensedComponents:
          normalizeLicensedComponents(
            (product as LicensedProductDto & { licensedComponents?: unknown })
              .licensedComponents,
          ) ?? [],
      });

      const upsertProduct = (
        products: LicensedProductDto[],
        product: LicensedProductDto,
        fallbackProductId?: string,
      ): LicensedProductDto[] => {
        const normalizedProduct = normalizeProduct(product);
        const resolvedProductId = product.id ?? fallbackProductId ?? '';
        if (!resolvedProductId) {
          return [...products, normalizedProduct];
        }

        let foundMatch = false;
        const updatedProducts = products.map((existingProduct) => {
          if (existingProduct.id !== resolvedProductId) {
            return existingProduct;
          }
          foundMatch = true;
          return {
            ...existingProduct,
            ...normalizedProduct,
            id: resolvedProductId,
          };
        });

        if (foundMatch) {
          return updatedProducts;
        }

        return [
          ...updatedProducts,
          { ...normalizedProduct, id: resolvedProductId },
        ];
      };

      const fetchProduct$ = ({
        productId,
      }: GetLicensedProductPayload): Observable<LicensedProductDto> => {
        patchState(store, { loading: true, error: null });
        return licensedProductService.getById(productId).pipe(
          take(1),
          tap({
            next: (product) => {
              const normalizedProduct = normalizeProduct(product);
              patchState(store, {
                products: upsertProduct(
                  store.products(),
                  normalizedProduct,
                  productId,
                ),
                selectedProduct: normalizedProduct,
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          }),
        );
      };

      return {
        getAllProducts(): void {
          patchState(store, { loading: true, error: null });
          licensedProductService
            .getAll()
            .pipe(take(1))
            .subscribe({
              next: (result) => {
                const normalizedProducts = (result.list ?? []).map(
                  normalizeProduct,
                );
                patchState(store, {
                  products: normalizedProducts,
                  loading: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, loading: false });
              },
            });
        },

        getComponentsForLicensedProduct(): void {
          patchState(store, { loading: true, error: null });
          licensedProductService
            .getComponentsForLicensedProduct()
            .pipe(take(1))
            .subscribe({
              next: (result) => {
                patchState(store, {
                  availableComponents: normalizeAvailableComponents(
                    result.list ?? [],
                  ),
                  loading: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, loading: false });
              },
            });
        },

        getProduct(payload: GetLicensedProductPayload): void {
          fetchProduct$(payload).subscribe();
        },

        getProductFromStoreOrFetch({
          productId,
        }: GetLicensedProductPayload): Observable<LicensedProductDto> {
          const cachedProduct =
            store.products().find((product) => product.id === productId) ??
            null;
          if (cachedProduct) {
            patchState(store, { error: null, selectedProduct: cachedProduct });
            return of(cachedProduct);
          }

          return fetchProduct$({ productId });
        },

        createProduct(payload: LicensedProductDto): void {
          patchState(store, { saving: true, error: null });
          licensedProductService
            .create(payload)
            .pipe(take(1))
            .subscribe({
              next: (product) => {
                const normalizedProduct = normalizeProduct(product);
                patchState(store, {
                  products: upsertProduct(
                    store.products(),
                    normalizedProduct,
                    payload.id,
                  ),
                  selectedProduct: normalizedProduct,
                  saving: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, saving: false });
              },
            });
        },

        updateProduct({
          productId,
          payload,
        }: UpdateLicensedProductPayload): void {
          patchState(store, { saving: true, error: null });
          licensedProductService
            .update(productId, payload)
            .pipe(take(1))
            .subscribe({
              next: (product) => {
                const normalizedProduct = normalizeProduct(product);
                patchState(store, {
                  products: upsertProduct(
                    store.products(),
                    normalizedProduct,
                    productId,
                  ),
                  selectedProduct: {
                    ...normalizedProduct,
                    id: product.id ?? productId,
                  },
                  saving: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, saving: false });
              },
            });
        },

        deleteProduct(productId: string): void {
          patchState(store, { saving: true, error: null });
          licensedProductService
            .delete(productId)
            .pipe(take(1))
            .subscribe({
              next: () => {
                patchState(store, {
                  products: store
                    .products()
                    .filter((product) => product.id !== productId),
                  selectedProduct:
                    store.selectedProduct()?.id === productId
                      ? null
                      : store.selectedProduct(),
                  saving: false,
                });
              },
              error: (error: unknown) => {
                patchState(store, { error, saving: false });
              },
            });
        },

        clearError(): void {
          patchState(store, { error: null });
        },

        reset(): void {
          patchState(store, initialState);
        },
      };
    },
  ),
);
